import { z } from 'zod';
import { lenientString, lenientUuid, lenientDateString, lenientNumber, lenientOptionalNode } from '../schema-extensions';
import { PersonPronounNode } from './person-pronoun-node-schema';

/**
 * Validation constants for PersonNode
 * Defined before schemas to avoid forward reference issues
 */
const PERSON_CONSTANTS = {
  NAME_MAX_LENGTH: 100,
  DATE_FORMAT_LENGTH: 10,
  MIN_RETIREMENT_AGE: 21,
  MAX_RETIREMENT_AGE: 85,
  DEFAULT_RETIREMENT_AGE: 67,
} as const;

/**
 * Lenient schema for deserializing PersonNode from storage
 * Handles missing fields and data migrations gracefully
 */
const PersonNodeDeserializeSchema = z.object({
  id: lenientUuid(),
  forename: lenientString(PERSON_CONSTANTS.NAME_MAX_LENGTH, ''),
  surname: lenientString(PERSON_CONSTANTS.NAME_MAX_LENGTH, ''),
  pronouns: lenientOptionalNode(PersonPronounNode),
  dateOfBirth: lenientDateString(''),
  targetRetirementAge: lenientNumber(PERSON_CONSTANTS.DEFAULT_RETIREMENT_AGE, PERSON_CONSTANTS.MIN_RETIREMENT_AGE, PERSON_CONSTANTS.MAX_RETIREMENT_AGE),
  archiveDate: z.string().optional().catch(undefined).transform(val => val?.slice(0, 10))
}).transform(val => ({
  id: val.id,
  forename: val.forename,
  surname: val.surname,
  pronouns: val.pronouns,
  dateOfBirth: val.dateOfBirth,
  targetRetirementAge: val.targetRetirementAge,
  archiveDate: val.archiveDate
}));

/**
 * Strict schema for creating new PersonNode
 * Enforces validation rules for new data
 */
const PersonNodeCreateSchema = z.object({
  id: z.string().uuid(),
  forename: z.string().min(1, 'Forename is required').max(PERSON_CONSTANTS.NAME_MAX_LENGTH),
  surname: z.string().min(1, 'Surname is required').max(PERSON_CONSTANTS.NAME_MAX_LENGTH),
  pronouns: z.preprocess(
    (val) => val === undefined ? undefined : PersonPronounNode.create(val as Partial<PersonPronounNode>),
    z.custom<PersonPronounNode>().optional()
  ),
  dateOfBirth: z.string().length(PERSON_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format'),
  targetRetirementAge: z.number().min(PERSON_CONSTANTS.MIN_RETIREMENT_AGE).max(PERSON_CONSTANTS.MAX_RETIREMENT_AGE),
  archiveDate: z.string().length(PERSON_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format').optional()
}).transform(val => ({
  id: val.id,
  forename: val.forename,
  surname: val.surname,
  pronouns: val.pronouns,
  dateOfBirth: val.dateOfBirth,
  targetRetirementAge: val.targetRetirementAge,
  archiveDate: val.archiveDate
}));

/**
 * PersonNode interface
 * Represents a person in the vault (family member, etc.)
 */
export interface PersonNode {
  id: string;
  forename: string;
  surname: string;
  pronouns?: PersonPronounNode;
  dateOfBirth: string;
  targetRetirementAge: number;
  archiveDate?: string;
}

/**
 * PersonNode namespace with factory and deserialization methods
 */
export namespace PersonNode {
  /**
   * Validation constants for PersonNode
   */
  export const Constants = PERSON_CONSTANTS;

  /**
   * Deserialize PersonNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid PersonNode with defaults applied
   */
  export function deserialize(data: unknown): PersonNode {
    return PersonNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize PersonNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: PersonNode }
    | { success: false; error: z.ZodError } 
  {
    const result = PersonNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new PersonNode with strict validation
   * Use this for creating new people from user input
   * 
   * @param data Partial person data
   * @returns Valid PersonNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<PersonNode>): PersonNode {
    return PersonNodeCreateSchema.parse({
      id: data.id ?? crypto.randomUUID(),
      ...data
    });
  }

  /**
   * Validate if data conforms to PersonNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid PersonNode
   */
  export function validate(data: unknown): data is PersonNode {
    return PersonNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Get full name of person
   * 
   * @param node PersonNode to get name for
   * @returns Full name (forename + surname)
   */
  export function getFullName(node: PersonNode): string {
    return `${node.forename} ${node.surname}`.trim();
  }

  /**
   * Calculate age based on date of birth
   * 
   * @param node PersonNode to calculate age for
   * @param referenceDate Date to calculate age at (defaults to today)
   * @returns Age in years
   */
  export function calculateAge(node: PersonNode, referenceDate: Date = new Date()): number {
    if (!node.dateOfBirth) return 0;
    
    const birthDate = new Date(node.dateOfBirth);
    let age = referenceDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  /**
   * Calculate years until retirement
   * 
   * @param node PersonNode to calculate for
   * @param referenceDate Date to calculate from (defaults to today)
   * @returns Years until retirement (negative if already retired)
   */
  export function yearsUntilRetirement(node: PersonNode, referenceDate: Date = new Date()): number {
    const currentAge = calculateAge(node, referenceDate);
    return node.targetRetirementAge - currentAge;
  }

  export function isActive(node: PersonNode, referenceDate: Date = new Date()): boolean {
    return !node.archiveDate || new Date(node.archiveDate) > referenceDate;
  }
}